import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PageSEO, OrganizationSchema, WebSiteSchema, LocalBusinessSchema } from "@/components/SEO";

// Pages
import HomePage from "@/pages/HomePage";
import LoadBoard from "@/pages/LoadBoard";
import ServicesPage from "@/pages/ServicesPage";
import ContactPage from "@/pages/ContactPage";
import CareerPage from "@/pages/CareerPage";
import QuotePage from "@/pages/QuotePage";
import QuoteCalculatorPage from "@/pages/QuoteCalculatorPage";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import CarrierRegisterPage from "@/pages/CarrierRegisterPage";
import UnsubscribePage from "@/pages/UnsubscribePage";
import { BlogListPage, BlogArticlePage } from "@/pages/BlogPage";
import SSOCallbackPage from "@/pages/SSOCallbackPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";

// Layout
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import ScrollToTop from "@/components/ScrollToTop";
import FloatingChatbot from "@/components/FloatingChatbot";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-red-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!user || user === false) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Public Route - redirects to dashboard if already logged in
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-red-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (user && user !== false) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<PageSEO path="/"><HomePage /></PageSEO>} />
      <Route path="/load-board" element={<PageSEO path="/load-board"><LoadBoard /></PageSEO>} />
      <Route path="/services" element={<PageSEO path="/services"><ServicesPage /></PageSEO>} />
      <Route path="/services/:serviceType" element={<ServicesPage />} />
      <Route path="/contact" element={<PageSEO path="/contact"><ContactPage /></PageSEO>} />
      <Route path="/careers" element={<PageSEO path="/careers"><CareerPage /></PageSEO>} />
      <Route path="/quote" element={<PageSEO path="/quote"><QuotePage /></PageSEO>} />
      <Route path="/calculator" element={<PageSEO path="/calculator"><QuoteCalculatorPage /></PageSEO>} />
      <Route path="/carrier" element={<PageSEO path="/carrier"><CarrierRegisterPage /></PageSEO>} />
      <Route path="/blog" element={<PageSEO path="/blog"><BlogListPage /></PageSEO>} />
      <Route path="/blog/:slug" element={<BlogArticlePage />} />
      <Route path="/privacy" element={<PageSEO path="/privacy"><PrivacyPolicyPage /></PageSEO>} />
      <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
      <Route path="/unsubscribe" element={<UnsubscribePage />} />
      <Route path="/sso-callback" element={<SSOCallbackPage />} />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <div className="App min-h-screen flex flex-col bg-white">
        <BrowserRouter>
          <AuthProvider>
            <OrganizationSchema />
            <WebSiteSchema />
            <LocalBusinessSchema />
            <ScrollToTop />
            <Navbar />
            <main className="flex-1">
              <AppRoutes />
            </main>
            <Footer />
            <FloatingChatbot />
            <Toaster position="top-right" richColors />
          </AuthProvider>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
